import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Plus, Trash2, Search, Printer, Save, ArrowLeft,
  Upload, Check, Link, Languages, Copy, X, ChevronUp, ChevronDown, Calculator,
} from 'lucide-react';
import EstimateCostingModal from '../components/EstimateCostingModal';
import api from '../utils/api';
import dayjs from 'dayjs';

import { SUPPLIER, L } from '../utils/estimateLabels';
import { shareablePdfUrl } from '../utils/pdfLink';

// 수량/단위 2쌍 — 예: 10 SET × 50 EA
const emptyItem = () => ({ category: '', title: '', spec: '', qty: 1, unit: '', qty2: '', unit2: '', unitPrice: 0, amount: 0, note: '' });

/**
 * 금액 = 수량1 × 수량2 × 단가
 * 수량2를 비워두면 1로 계산한다 — 수량이 하나뿐인 기존 견적서의 금액이 그대로 유지되도록.
 * 단, 0을 명시한 경우는 0으로 계산한다.
 */
function calcAmount(it) {
  const blank = v => v === '' || v === null || v === undefined;
  const q1 = Number(it.qty) || 0;
  const q2 = blank(it.qty2) ? 1 : (Number(it.qty2) || 0);
  return Math.round(q1 * q2 * (Number(it.unitPrice) || 0));
}

const STATUS_OPTIONS = [
  { value: 'draft', label: '작성중' },
  { value: 'sent', label: '발송완료' },
  { value: 'accepted', label: '수락됨' },
  { value: 'rejected', label: '거절됨' },
];

/* ─────────── 거래처 검색 ─────────── */
function PartnerSearch({ value, onChange, placeholder }) {
  const [q, setQ] = useState(value || '');
  const [allPartners, setAllPartners] = useState([]);
  const [show, setShow] = useState(false);

  useEffect(() => { setQ(value || ''); }, [value]);

  useEffect(() => {
    api.get('/partners').then(res => {
      const data = res.data;
      setAllPartners(Array.isArray(data) ? data : (data.rows || []));
    }).catch(() => {});
  }, []);

  const filtered = q.trim()
    ? allPartners.filter(p =>
        p.name.toLowerCase().includes(q.toLowerCase()) ||
        (p.bizRegNo || '').includes(q) ||
        (p.contactName || '').toLowerCase().includes(q.toLowerCase())
      )
    : allPartners;

  return (
    <div style={{ position: 'relative', flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input
          value={q}
          onChange={e => { setQ(e.target.value); if (!e.target.value) onChange(null); }}
          onFocus={() => setShow(true)}
          onBlur={() => setTimeout(() => setShow(false), 150)}
          placeholder={placeholder}
          style={{ ...inpStyle, fontWeight: 600 }}
        />
        <Search size={11} style={{ color: '#94a3b8', flexShrink: 0 }} />
      </div>
      {show && filtered.length > 0 && (
        <div style={{
          position: 'absolute', zIndex: 50, left: 0, right: 0, top: '100%', marginTop: 4,
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 200, overflowY: 'auto',
        }}>
          {filtered.map(p => (
            <button key={p.id} type="button"
              onMouseDown={() => { onChange(p); setQ(p.name); setShow(false); }}
              style={{ width: '100%', textAlign: 'left', padding: '7px 12px', fontSize: 11, borderBottom: '1px solid #f1f5f9', background: 'none', cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              <span style={{ fontWeight: 600, color: '#1e293b' }}>{p.name}</span>
              {p.bizRegNo && <span style={{ color: '#94a3b8', marginLeft: 8, fontSize: 10 }}>{p.bizRegNo}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── 편집 인풋 공통 스타일 ── */
const inpStyle = {
  border: '1px solid transparent',
  borderRadius: 4,
  padding: '3px 6px',
  fontSize: 11,
  outline: 'none',
  background: 'transparent',
  fontFamily: 'inherit',
  color: '#1e293b',
  width: '100%',
  transition: 'border-color 0.15s, background 0.15s',
};

/**
 * 로고를 흰색으로 칠한 data URL을 만든다.
 * 인쇄 CSS는 filter: brightness(0) invert(1) 로 흰색을 만드는데,
 * html2canvas는 CSS filter를 지원하지 않아 PDF에서 원본(남색)으로 찍힌다.
 * → 픽셀을 직접 흰색으로 바꿔 미리 주입한다.
 */
async function whiteLogoDataUrl(src) {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = src; });
  const c = document.createElement('canvas');
  c.width = img.naturalWidth; c.height = img.naturalHeight;
  const ctx = c.getContext('2d');
  ctx.drawImage(img, 0, 0);
  const d = ctx.getImageData(0, 0, c.width, c.height);
  const px = d.data;
  for (let i = 0; i < px.length; i += 4) {
    // 알파는 그대로 두고 색만 흰색으로 (안티에일리어싱 유지)
    px[i] = 255; px[i + 1] = 255; px[i + 2] = 255;
  }
  ctx.putImageData(d, 0, 0);
  return c.toDataURL('image/png');
}

/* ── 인쇄 규격 (print CSS의 @page margin: 18mm 20mm 과 일치시킨다) ──
   PDF를 인쇄와 다른 폭으로 그리면 줄바꿈·행 높이·글자 비율이 달라져
   "글자가 셀 아래에 붙는" 것처럼 보인다. 인쇄 본문 폭과 똑같이 렌더한다. */
const PAGE_W_MM  = 210;
const MARGIN_X_MM = 20;
const MARGIN_Y_MM = 18;
const PRINT_W_PX = Math.round((PAGE_W_MM - MARGIN_X_MM * 2) / 25.4 * 96);  // 170mm → 643px

/**
 * 다운로드·메일 첨부용 파일명 = 제목_출력날짜
 * 윈도우/맥에서 금지되는 문자를 제거하고 길이를 제한한다.
 */
function buildFileName(form, estimateNo) {
  const raw = (form.title || '').trim() || estimateNo || '견적서';
  const safe = raw
    .replace(/[\\/:*?"<>|]/g, '')   // 파일명 금지 문자
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80);
  return `${safe || '견적서'}_${dayjs().format('YYYY-MM-DD')}`;
}

/* ── 인쇄 HTML 생성 ── */
export function buildPrintHtml(form, estimateNo, lang, logoUrl) {
  const l = L[lang];
  const s = SUPPLIER[lang];

  const fmtDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    if (lang === 'ko') return `${dt.getFullYear()}년 ${String(dt.getMonth()+1).padStart(2,'0')}월 ${String(dt.getDate()).padStart(2,'0')}일`;
    const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    return `${months[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
  };

  const curr = l.curr;
  const items = form.items.filter(it => it.title || it.amount > 0);
  const th = l.th;

  // 연속된 같은 구분끼리 rowspan 병합 — 각 그룹의 첫 행에만 구분 셀 출력
  const spanAt = {};   // 그룹 시작 인덱스 → 묶을 행 수
  const skip = new Set(); // 구분 셀을 생략할 행 인덱스
  for (let i = 0; i < items.length; i++) {
    if (skip.has(i)) continue;
    const cat = (items[i].category || '').trim();
    let n = 1;
    // 빈 구분은 병합하지 않음
    if (cat) {
      while (i + n < items.length && (items[i + n].category || '').trim() === cat) {
        skip.add(i + n);
        n++;
      }
    }
    spanAt[i] = n;
  }

  const rows = items.map((it, idx) => `
    <tr>
      ${skip.has(idx) ? '' : `<td class="center cat-cell"${spanAt[idx] > 1 ? ` rowspan="${spanAt[idx]}"` : ''}>${it.category || ''}</td>`}
      <td class="left pl8">${it.title || ''}</td>
      <td class="center">${it.spec || ''}</td>
      <td class="center">${it.qty ?? ''}</td>
      <td class="center">${it.unit || ''}</td>
      <td class="center">${it.qty2 ?? ''}</td>
      <td class="center">${it.unit2 || ''}</td>
      <td class="right pr8">${Number(it.unitPrice||0).toLocaleString()}</td>
      <td class="right pr8 fw6">${curr(it.amount)}</td>
      <td class="left pl6 note-cell">${it.note || ''}</td>
    </tr>`).join('');

  return `<!DOCTYPE html>
<html lang="${lang}">
<head>
<meta charset="UTF-8">
<!-- 모바일에서 인쇄하면 기기 화면 폭으로 레이아웃돼 표가 찌그러지고 2페이지로 넘어간다.
     A4 폭(96dpi 기준 794px)으로 고정해 기기와 무관하게 같은 결과가 나오게 한다. -->
<meta name="viewport" content="width=794">
<title>${buildFileName(form, estimateNo)}</title>
<style>
  @page { margin: 18mm 20mm; size: A4; }
  * { box-sizing: border-box; margin: 0; padding: 0; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  /* @page margin(좌우 20mm)을 뺀 A4 본문 폭. 화면 폭에 끌려가지 않도록 고정한다. */
  body { font-family: 'Malgun Gothic','Apple SD Gothic Neo','Noto Sans KR',sans-serif; font-size: 10pt; color: #0f172a; background: white; width: 170mm; margin: 0 auto; }

  /* 품목 행이 페이지 경계에서 잘리면 글자가 반쪽만 보인다 */
  tr, .info-box, .totals, .bank-box { page-break-inside: avoid; break-inside: avoid; }
  thead { display: table-header-group; }

  /* HEADER */
  .doc-header { background: linear-gradient(135deg, #0f172a 0%, #1e3355 100%) !important; padding: 20px 28px 16px; display: flex; align-items: center; gap: 0; position: relative; overflow: hidden; }
  .doc-header::before { content: ''; position: absolute; top: -30%; right: -5%; width: 200px; height: 200px; border-radius: 50%; background: radial-gradient(circle, rgba(0,178,255,0.15) 0%, transparent 70%); pointer-events: none; }
  .logo-wrap { background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.12); border-radius: 8px; padding: 8px 14px; display: flex; align-items: center; justify-content: center; }
  .logo-wrap img { height: 32px; object-fit: contain; filter: brightness(0) invert(1); }
  .doc-title { flex: 1; text-align: center; }
  .doc-title h1 { font-size: 28pt; font-weight: 900; letter-spacing: 0.26em; margin: 0; color: #ffffff; }
  .doc-meta { text-align: right; min-width: 150px; }
  .doc-meta .no { font-family: 'Courier New', monospace; font-size: 10pt; color: #00B2FF; font-weight: 700; }
  .doc-meta .date-label { font-size: 8pt; color: rgba(255,255,255,0.5); margin-top: 6px; }
  .doc-meta .date-val { font-size: 9pt; color: rgba(255,255,255,0.9); }

  /* CONTENT */
  .doc-content { padding: 22px 28px 24px; }

  /* INFO SECTIONS */
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 18px; }
  .info-box { border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; border-left: 3px solid #00B2FF; }
  .info-box-head { padding: 7px 12px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; display: flex; align-items: center; gap: 6px; }
  .info-box-head .dot { width: 6px; height: 6px; border-radius: 50%; background: #00B2FF; flex-shrink: 0; }
  .info-box-head span { font-size: 8.5pt; font-weight: 800; letter-spacing: 0.12em; color: #334155; }
  .info-row { display: flex; align-items: flex-start; padding: 5px 12px; border-bottom: 1px solid #f1f5f9; font-size: 9.5pt; }
  .info-row:last-child { border-bottom: none; }
  .info-label { color: #64748b; width: 76px; flex-shrink: 0; font-size: 8.5pt; padding-top: 1px; }
  .info-value { color: #0f172a; font-weight: 600; flex: 1; }
  .info-value.sm { font-size: 8.5pt; }

  /* TITLE LINE */
  .doc-subtitle { font-size: 10pt; color: #334155; margin-bottom: 14px; padding: 0 2px; font-style: italic; }

  /* TABLE */
  .items-wrap { border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; margin-bottom: 18px; }
  table { border-collapse: collapse; width: 100%; }
  thead tr { background: linear-gradient(135deg, #0f172a 0%, #1e3355 100%) !important; }
  thead th { font-size: 8.5pt; font-weight: 700; color: #e2e8f0; padding: 10px 4px; letter-spacing: 0.05em; border-right: 1px solid rgba(255,255,255,0.1); white-space: nowrap; line-height: 1; vertical-align: middle; }
  thead th:last-child { border-right: none; }
  tbody tr { border-bottom: 1px solid #f1f5f9; }
  tbody tr:last-child { border-bottom: none; }
  tbody td { font-size: 9.5pt; padding: 9px 4px; border-right: 1px solid #f1f5f9; line-height: 1; vertical-align: middle; }
  tbody td:last-child { border-right: none; }
  .center { text-align: center; white-space: nowrap; }
  .note-cell { white-space: nowrap; }
  /* 구분 병합 셀 — 세로 가운데 정렬 + 그룹 경계선 */
  .cat-cell { vertical-align: middle; border-right: 1px solid #e2e8f0 !important; }
  .left { text-align: left; }
  .right { text-align: right; }
  .pl8 { padding-left: 8px; }
  .pr8 { padding-right: 8px; }
  .pl6 { padding-left: 6px; }
  .fw6 { font-weight: 600; }

  /* BOTTOM */
  .bottom-row { display: flex; gap: 16px; margin-bottom: 18px; align-items: flex-start; }
  .memo-box { flex: 1; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 12px; min-height: 60px; font-size: 9pt; color: #475569; line-height: 1.5; white-space: pre-wrap; word-break: break-word; }
  .bank-holder { font-size: 9.5pt; font-weight: 700; color: #0f172a; }
  .totals { border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; min-width: 210px; border-left: 3px solid #00B2FF; }
  .totals-row { display: flex; justify-content: space-between; padding: 7px 14px; font-size: 9.5pt; border-bottom: 1px solid #f1f5f9; }
  .totals-row:last-child { background: linear-gradient(135deg, #0f172a 0%, #1e3355 100%) !important; border-bottom: none; padding: 10px 14px; }
  .totals-row:last-child span { color: white; font-weight: 800; font-size: 11pt; }
  .totals-row:last-child .tv { color: #00B2FF; }
  .tl { color: #64748b; }
  .tv { font-weight: 600; color: #0f172a; }

  /* BANK */
  .bank-wrap { display: inline-block; white-space: nowrap; background: #f8fafc; border: 1px solid #e2e8f0; border-left: 3px solid #00B2FF; border-radius: 8px; padding: 10px 18px; font-size: 9.5pt; }
  .bank-label { color: #64748b; font-weight: 700; letter-spacing: 0.04em; margin-right: 10px; }
  .bank-val { color: #0f172a; font-family: 'Courier New', monospace; font-weight: 700; margin-right: 10px; }

</style>
</head>
<body>

<div class="doc-header">
  <div class="logo-wrap"><img src="${logoUrl}" alt="Prinmann" /></div>
  <div class="doc-title">
    <h1>${l.docTitle}</h1>
  </div>
  <div class="doc-meta">
    <div class="no">No. ${estimateNo || '—'}</div>
    <div class="date-label">${l.date}</div>
    <div class="date-val">${fmtDate(form.date)}</div>
  </div>
</div>

<div class="doc-content">

  <div class="info-grid">
    <div class="info-box">
      <div class="info-box-head"><div class="dot"></div><span>${l.billTo}</span></div>
      <div class="info-row"><span class="info-label">${l.f_name}</span><span class="info-value">${form.partnerName || '—'}</span></div>
      <div class="info-row"><span class="info-label">${l.f_ceo}</span><span class="info-value">${form.partnerCeo || '—'}</span></div>
      <div class="info-row"><span class="info-label">${l.f_regNo}</span><span class="info-value">${form.partnerBizRegNo || '—'}</span></div>
      <div class="info-row"><span class="info-label">${l.f_address}</span><span class="info-value sm">${form.partnerAddress || '—'}</span></div>
      <div class="info-row"><span class="info-label">${l.f_bizType}</span><span class="info-value">${form.partnerBizType || '—'}${(form.partnerBizType && form.partnerBizItem) ? '&nbsp;/&nbsp;' + form.partnerBizItem : (!form.partnerBizType && form.partnerBizItem ? form.partnerBizItem : '')}</span></div>
      <div class="info-row"><span class="info-label">${l.f_phone}</span><span class="info-value">${form.partnerPhone || '—'}</span></div>
    </div>
    <div class="info-box">
      <div class="info-box-head"><div class="dot"></div><span>${l.issuedBy}</span></div>
      <div class="info-row"><span class="info-label">${l.f_name}</span><span class="info-value">${s.name}</span></div>
      <div class="info-row"><span class="info-label">${l.f_ceo}</span><span class="info-value">${s.ceo}</span></div>
      <div class="info-row"><span class="info-label">${l.f_regNo}</span><span class="info-value">${s.bizRegNo}</span></div>
      <div class="info-row"><span class="info-label">${l.f_address}</span><span class="info-value sm">${s.address}</span></div>
      <div class="info-row"><span class="info-label">${l.f_bizType}</span><span class="info-value">${s.bizType}&nbsp;/&nbsp;${s.bizItem}</span></div>
      <div class="info-row"><span class="info-label">${l.f_phone}</span><span class="info-value">${s.phone}</span></div>
    </div>
  </div>

  ${form.title ? `<div class="doc-subtitle">${form.title}</div>` : ''}

  <div class="items-wrap">
    <table>
      <thead>
        <tr>
          <!-- 품목명(th[1])은 폭을 지정하지 않아 남는 공간을 모두 차지한다.
               나머지를 좁혀 품목명과 구분에 폭을 몰아준다. -->
          <th style="width:46px">${th[0]}</th>
          <th style="text-align:left;padding-left:8px">${th[1]}</th>
          <th style="width:38px">${th[2]}</th>
          <th style="width:30px">${th[3]}</th>
          <th style="width:32px">${th[4]}</th>
          <th style="width:30px">${th[5]}</th>
          <th style="width:32px">${th[6]}</th>
          <th style="width:68px;text-align:right;padding-right:6px">${th[7]}</th>
          <th style="width:78px;text-align:right;padding-right:6px">${th[8]}</th>
          <th style="width:66px">${th[9]}</th>
        </tr>
      </thead>
      <tbody>
        ${rows || `<tr><td colspan="10" style="text-align:center;padding:20px;color:#94a3b8">—</td></tr>`}
      </tbody>
    </table>
  </div>

  <div class="bottom-row">
    ${form.memo ? `<div class="memo-box">${form.memo}</div>` : '<div style="flex:1"></div>'}
    <div class="totals">
      <div class="totals-row"><span class="tl">${l.subtotal}</span><span class="tv">${curr(form.subtotal)}</span></div>
      ${form.nego > 0 ? `<div class="totals-row"><span class="tl">${lang === 'ko' ? '절사' : 'Rounding'}</span><span class="tv" style="color:#ef4444">- ${curr(form.nego)}</span></div>` : ''}
      ${form.nego > 0 && form.vatIncluded ? `<div class="totals-row"><span class="tl">${l.sum}</span><span class="tv">${curr(form.subtotal - form.nego)}</span></div>` : ''}
      ${form.vatIncluded ? `<div class="totals-row"><span class="tl">${l.vat}</span><span class="tv">${curr(form.vat)}</span></div>` : ''}
      <div class="totals-row"><span>${l.total}</span><span class="tv">${curr(form.total)}</span></div>
    </div>
  </div>

  <div class="bank-wrap">
    <span class="bank-label">${l.bank}</span>
    <span class="bank-val">${s.bank}</span>
    <span class="bank-holder">${s.name}</span>
  </div>


</div>

<script>window.onload = function(){ window.print(); }</script>
</body>
</html>`;
}

/* ─────────── 메인 컴포넌트 ─────────── */
/* ── 기존 견적서 선택 모달 ── */
function EstimatePicker({ excludeId, onPick, onClose }) {
  const [list, setList] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => {
      api.get('/estimates', { params: { page: 1, limit: 30, q } })
        .then(res => { if (alive) setList(res.data.rows || res.data.estimates || []); })
        .catch(() => { if (alive) setList([]); })
        .finally(() => { if (alive) setLoading(false); });
    }, q ? 250 : 0);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);

  const rows = list.filter(e => String(e.id) !== String(excludeId));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-2xl h-[75vh] flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Copy size={15} className="text-brand" />
            <h3 className="font-bold text-gray-100">기존 견적서 불러오기</h3>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300"><X size={18} /></button>
        </div>

        <div className="px-5 pt-4 flex-shrink-0">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              autoFocus
              placeholder="거래처명, 건명으로 검색"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-9 pr-3 py-2 text-sm text-white caret-white focus:outline-none focus:border-brand placeholder-gray-500"
            />
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-3">
          {loading ? (
            <div className="flex justify-center py-12">
              <span className="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin" />
            </div>
          ) : rows.length === 0 ? (
            <p className="text-center text-sm text-gray-600 py-12">
              {q ? '검색 결과가 없습니다' : '불러올 견적서가 없습니다'}
            </p>
          ) : (
            <div className="flex flex-col gap-1.5">
              {rows.map(e => (
                <button
                  key={e.id}
                  onClick={() => onPick(e.id)}
                  className="text-left rounded-xl border border-gray-800 bg-gray-800/40 px-4 py-3 hover:border-brand/50 hover:bg-gray-800 transition-colors"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-mono text-[11px] text-brand font-semibold">{e.estimateNo || '번호없음'}</span>
                    <span className="text-[11px] text-gray-500">{e.date ? dayjs(e.date).format('YY.MM.DD') : '-'}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3 mt-1">
                    <span className="text-sm font-medium text-white truncate">
                      {e.partnerName || '거래처 미입력'}
                    </span>
                    <span className="text-sm font-semibold text-white whitespace-nowrap">
                      {e.total ? Number(e.total).toLocaleString() + '원' : '-'}
                    </span>
                  </div>
                  {e.title && <div className="text-xs text-gray-500 truncate mt-0.5">{e.title}</div>}
                </button>
              ))}
            </div>
          )}
        </div>

        <p className="px-5 pb-4 text-[11px] text-gray-600 flex-shrink-0">
          거래처 정보·건명·품목·메모를 가져옵니다. 현재 작성 중인 내용은 덮어쓰며,
          날짜와 견적번호는 그대로 유지됩니다.
        </p>
      </div>
    </div>
  );
}

export default function EstimateWritePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEdit = !!id;
  const copyFromId = searchParams.get('copyFrom');
  const [pickerOpen, setPickerOpen] = useState(false);
  // 원가계산 모달을 연 품목의 행 번호 (null이면 닫힘)
  const [costingIdx, setCostingIdx] = useState(null);

  const [lang, setLang] = useState('ko');
  const l = L[lang];
  const s = SUPPLIER[lang];

  const [form, setForm] = useState({
    date: dayjs().format('YYYY-MM-DD'),
    partnerName: '', partnerCeo: '',
    partnerBizRegNo: '', partnerAddress: '',
    partnerBizType: '', partnerBizItem: '', partnerPhone: '',
    title: '',
    items: [emptyItem()],
    memo: '',
    subtotal: 0, nego: 0, vat: 0, total: 0,
    vatIncluded: true,
    status: 'draft',
  });
  const [estimateNo, setEstimateNo] = useState('');
  const [savedId, setSavedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfUploading, setPdfUploading] = useState(false);
  const [copied, setCopied] = useState(false);

  /**
   * 견적서 데이터를 폼에 채운다.
   *
   * 'edit'    — 내 견적서를 여는 경우. 견적번호·저장ID·PDF까지 그대로 이어받는다.
   * 'copy'    — ?copyFrom= 로 진입한 복사본. 날짜는 오늘, 상태는 작성중으로 초기화.
   * 'content' — 작성 중 "불러오기". 내용만 바꾸고 견적번호·날짜·상태·PDF는 현재 것을 유지해
   *             이미 저장된 건이 실수로 새 건으로 갈라지지 않게 한다.
   */
  const fillForm = (e, mode) => {
    setForm(prev => ({
      date: mode === 'edit' ? (e.date || dayjs().format('YYYY-MM-DD'))
          : mode === 'copy' ? dayjs().format('YYYY-MM-DD')
          : prev.date,
      partnerName: e.partnerName || '',
      partnerCeo: e.partnerCeo || '',
      partnerBizRegNo: e.partnerBizRegNo || '',
      partnerAddress: e.partnerAddress || '',
      partnerBizType: e.partnerBizType || '',
      partnerBizItem: e.partnerBizItem || '',
      partnerPhone: e.partnerPhone || '',
      title: e.title || '',
      // items는 JSON 배열 — 원본과 참조를 공유하지 않도록 복사
      items: e.items?.length ? e.items.map(it => ({ ...it })) : [emptyItem()],
      memo: e.memo || '',
      subtotal: e.subtotal || 0,
      nego: e.nego || 0,
      vat: e.vat || 0,
      total: e.total || 0,
      vatIncluded: e.vatIncluded !== false,
      status: mode === 'edit' ? (e.status || 'draft')
            : mode === 'copy' ? 'draft'
            : prev.status,
    }));

    // 견적서의 신원(번호·저장ID·PDF)은 'edit'일 때만 원본을 따라간다.
    if (mode === 'edit') {
      setEstimateNo(e.estimateNo || '');
      setSavedId(e.id);
      setPdfUrl(e.pdfUrl || '');
    }
  };

  useEffect(() => {
    const srcId = isEdit ? id : copyFromId;
    if (!srcId) return;
    api.get(`/estimates/${srcId}`)
      .then(res => fillForm(res.data, isEdit ? 'edit' : 'copy'))
      .catch(() => navigate('/estimates'));
  }, [id, copyFromId]);

  useEffect(() => {
    const subtotal = form.items.reduce((s, it) => s + (Number(it.amount) || 0), 0);
    const nego = Number(form.nego) || 0;
    const vat = form.vatIncluded ? Math.floor((subtotal - nego) * 0.1) : 0;
    setForm(f => ({ ...f, subtotal, vat, total: subtotal - nego + vat }));
  }, [form.items, form.vatIncluded, form.nego]);

  const up = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function upItem(idx, key, val) {
    setForm(f => {
      const items = f.items.map((it, i) => {
        if (i !== idx) return it;
        const updated = { ...it, [key]: val };
        if (key === 'qty' || key === 'qty2' || key === 'unitPrice') {
          updated.amount = calcAmount(updated);
        }
        return updated;
      });
      return { ...f, items };
    });
  }

  function selectPartner(p) {
    if (!p) return;
    setForm(f => ({
      ...f,
      partnerName: p.name || '',
      partnerCeo: p.contactName || '',
      partnerBizRegNo: p.bizRegNo || '',
      partnerAddress: p.address || '',
      partnerBizType: p.bizType || '',
      partnerBizItem: p.bizItem || '',
      partnerPhone: p.contactPhone || '',
    }));
  }

  async function handleSave() {
    if (!form.partnerName?.trim()) {
      alert('거래처를 선택해주세요.');
      return;
    }
    setSaving(true);
    try {
      const cid = savedId || (isEdit ? id : null);
      if (cid) {
        await api.put(`/estimates/${cid}`, form);
      } else {
        const res = await api.post('/estimates', form);
        setSavedId(res.data.id);
        setEstimateNo(res.data.estimateNo);
        navigate(`/estimates/${res.data.id}`, { replace: true });
      }
    } catch (e) { alert('저장 실패: ' + (e.response?.data?.message || e.message)); }
    setSaving(false);
  }

  async function handlePdfUpload() {
    const cid = savedId || (isEdit ? id : null);
    if (!cid) { alert('먼저 저장해주세요.'); return; }
    setPdfUploading(true);
    try {
      // html2canvas 화면 캡처 대신 pdf-lib으로 벡터 PDF를 직접 그린다.
      // 캡처 방식은 글자 크기·기준선이 브라우저와 미묘하게 달라 인쇄본과 어긋났다.
      const { buildEstimatePdf } = await import('../utils/estimatePdf');

      let logoPngUrl = null;
      try { logoPngUrl = await whiteLogoDataUrl(`${window.location.origin}/prinmann_logo.png`); } catch {}

      const blob = await buildEstimatePdf({
        form, estimateNo, lang, L: L[lang], S: SUPPLIER[lang], logoPngUrl,
      });

      const fd = new FormData();
      fd.append('pdf', blob, `${buildFileName(form, estimateNo)}.pdf`);
      const res = await api.post(`/estimates/${cid}/pdf`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPdfUrl(res.data.pdfUrl);
      alert('PDF가 저장되었습니다.');
    } catch (e) {
      console.error('[estimate pdf]', e);
      alert('PDF 저장 실패: ' + e.message);
    }
    setPdfUploading(false);
  }

  function handlePrint() {
    const logoUrl = `${window.location.origin}/prinmann_logo.png`;
    const html = buildPrintHtml(form, estimateNo, lang, logoUrl);
    const win = window.open('', '_blank', 'width=960,height=750');
    win.document.write(html);
    win.document.close();
  }

  function handleCopyUrl() {
    const url = `${window.location.origin}${shareablePdfUrl(pdfUrl)}`;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
    } else {
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  const th = l.th;

  /* ── 셀 스타일 헬퍼 ── */
  const tdStyle = (align = 'center') => ({
    padding: '4px 3px', textAlign: align,
    borderRight: '1px solid #f1f5f9', verticalAlign: 'middle',
  });

  return (
    <div className="flex flex-col h-full">
      {/* ── 상단 액션 바 (다크 테마) ── */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/estimates')}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-white transition-colors">
            <ArrowLeft size={15} /> 목록
          </button>
          {estimateNo && (
            <span className="font-mono text-xs text-brand bg-brand/10 border border-brand/20 px-2.5 py-1 rounded-lg">{estimateNo}</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setPickerOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-700 rounded-xl text-gray-400 hover:text-brand hover:border-brand/40 transition-colors">
            <Copy size={12} /> 기존 견적서 불러오기
          </button>
          <button onClick={() => setLang(l => l === 'ko' ? 'en' : 'ko')}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-gray-700 rounded-xl text-gray-400 hover:text-brand hover:border-brand/40 transition-colors">
            <Languages size={12} />{lang === 'ko' ? 'EN' : 'KO'}
          </button>
          <select value={form.status} onChange={e => up('status', e.target.value)}
            className="bg-gray-900 border border-gray-700 rounded-xl px-2 py-1.5 text-xs text-gray-300 focus:outline-none focus:border-brand">
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {/* 모바일에서는 감춘다. 브라우저 인쇄는 URL·날짜 머리말이 함께 찍혀
              대외 발송용으로 쓸 수 없고, 폭도 기기에 따라 흔들린다.
              모바일에서는 결과가 일정한 PDF 저장만 쓰게 한다. */}
          <button onClick={handlePrint}
            className="hidden md:flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-700 rounded-xl text-gray-400 hover:text-white hover:bg-gray-800 transition-colors">
            <Printer size={12} /> 인쇄 / 미리보기
          </button>
          <button onClick={handlePdfUpload} disabled={pdfUploading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-700 rounded-xl text-gray-400 hover:text-white hover:bg-gray-800 transition-colors disabled:opacity-40">
            {pdfUploading
              ? <span className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin" />
              : <Upload size={12} />}
            PDF 저장
          </button>
          {pdfUrl && (
            <button onClick={handleCopyUrl}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-green-700/50 rounded-xl text-green-400 hover:bg-green-900/20 transition-colors">
              {copied ? <><Check size={12} /> 복사됨</> : <><Link size={12} /> PDF 링크</>}
            </button>
          )}
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-1.5 px-4 py-1.5 text-xs bg-brand text-white rounded-xl hover:bg-brand/80 disabled:opacity-50 font-medium transition-colors">
            <Save size={12} />{saving ? '저장중...' : '저장'}
          </button>
        </div>
      </div>

      {/* ── 견적서 제목 ── */}
      <div className="flex items-center gap-3 mb-3 flex-shrink-0">
        <span className="text-xs text-gray-500 whitespace-nowrap font-semibold">제목</span>
        <input
          value={form.title}
          onChange={e => up('title', e.target.value)}
          placeholder={l.defaultTitle}
          className="flex-1 bg-gray-900 border border-gray-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-brand placeholder-gray-600"
        />
      </div>

      {/* ── 견적서 문서 (편집 가능) ── */}
      <div className="flex-1 min-h-0 overflow-y-auto pb-4">
        <div
          id="estimate-doc"
          style={{
            maxWidth: 820, margin: '0 auto',
            fontFamily: "'Malgun Gothic','Apple SD Gothic Neo','Noto Sans KR',sans-serif",
            color: '#0f172a', background: '#fff',
            borderRadius: 16,
            overflow: 'hidden',
            boxShadow: '0 0 0 1px rgba(255,255,255,0.06), 0 8px 40px rgba(0,0,0,0.5)',
          }}
        >
          {/* 다크 헤더 */}
          <div style={{
            background: 'linear-gradient(135deg, #0f172a 0%, #1e3355 100%)',
            padding: '22px 32px 20px',
            display: 'flex', alignItems: 'center', gap: 0,
            position: 'relative', overflow: 'hidden',
          }}>
            {/* 헤더 글로우 */}
            <div style={{
              position: 'absolute', top: '-40%', right: '-2%',
              width: 240, height: 240, borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(0,178,255,0.12) 0%, transparent 70%)',
              pointerEvents: 'none',
            }} />

            {/* 로고 */}
            <div style={{
              background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 8, padding: '8px 14px', display: 'flex', alignItems: 'center', flexShrink: 0,
            }}>
              <img src="/prinmann_logo.png" alt="Prinmann"
                style={{ height: 30, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
            </div>

            {/* 타이틀 */}
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{
                fontSize: 30, fontWeight: 900, letterSpacing: '0.26em', lineHeight: 1,
                background: 'linear-gradient(90deg, #ffffff 0%, #a8d8ff 50%, #00B2FF 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
                {l.docTitle}
              </div>
            </div>

            {/* No / 날짜 */}
            <div style={{ textAlign: 'right', minWidth: 160, flexShrink: 0 }}>
              <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#00B2FF', fontWeight: 700 }}>
                No. {estimateNo || '—'}
              </div>
              <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.45)', whiteSpace: 'nowrap' }}>{l.date}</span>
                <input type="date" value={form.date} onChange={e => up('date', e.target.value)}
                  style={{
                    background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: 5, padding: '2px 6px', fontSize: 10, color: 'rgba(255,255,255,0.85)',
                    outline: 'none', fontFamily: 'inherit', width: 110,
                  }} />
              </div>
            </div>
          </div>

          {/* 문서 본문 */}
          <div style={{ padding: '24px 32px 32px' }}>

            {/* 수신처 + 공급자 — 통합 그리드 (행 자동 정렬) */}
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr',
              border: '1px solid #e2e8f0', borderLeft: '3px solid #00B2FF',
              borderRadius: 8, overflow: 'hidden', marginBottom: 18,
            }}>
              {/* 헤더 행 */}
              <DualHead left={l.billTo} right={l.issuedBy} />
              {/* 행1: 상호 */}
              <DualRow
                left={<PartnerSearch value={form.partnerName}
                  onChange={p => { if (p) selectPartner(p); else up('partnerName', ''); }}
                  placeholder={lang === 'ko' ? '거래처 검색...' : 'Search company...'} />}
                right={<StaticVal value={s.name} bold />}
                label={l.f_name}
              />
              {/* 행2: 대표자 */}
              <DualRow
                left={<EditInput value={form.partnerCeo} onChange={v => up('partnerCeo', v)} placeholder="-" />}
                right={<StaticVal value={s.ceo} />}
                label={l.f_ceo}
              />
              {/* 행3: 등록번호 */}
              <DualRow
                left={<EditInput value={form.partnerBizRegNo} onChange={v => up('partnerBizRegNo', v)} placeholder="000-00-00000" />}
                right={<StaticVal value={s.bizRegNo} />}
                label={l.f_regNo}
              />
              {/* 행4: 주소 */}
              <DualRow
                left={<EditInput value={form.partnerAddress} onChange={v => up('partnerAddress', v)} placeholder="" />}
                right={<StaticVal value={s.address} small />}
                label={l.f_address}
              />
              {/* 행5: 업태/종목 */}
              <DualRow
                left={
                  <div style={{ display: 'flex', gap: 4 }}>
                    <EditInput value={form.partnerBizType} onChange={v => up('partnerBizType', v)} placeholder="" />
                    <span style={{ color: '#cbd5e1', fontSize: 11, flexShrink: 0, alignSelf: 'center' }}>/</span>
                    <EditInput value={form.partnerBizItem} onChange={v => up('partnerBizItem', v)} placeholder="" />
                  </div>
                }
                right={<StaticVal value={`${s.bizType} / ${s.bizItem}`} />}
                label={l.f_bizType}
              />
              {/* 행6: 전화 */}
              <DualRow
                left={<EditInput value={form.partnerPhone} onChange={v => up('partnerPhone', v)} placeholder="-" />}
                right={<StaticVal value={s.phone} />}
                label={l.f_phone}
                last
              />
            </div>


            {/* 품목 테이블 */}
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', marginBottom: 18 }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11 }}>
                  <thead>
                    <tr style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3355 100%)' }}>
                      {[
                        { w: 50, label: th[0] },
                        { w: null, label: th[1], left: true },
                        { w: 56, label: th[2] },
                        { w: 46, label: th[3] },
                        { w: 40, label: th[4] },
                        { w: 46, label: th[5] },
                        { w: 40, label: th[6] },
                        { w: 86, label: th[7] },
                        { w: 92, label: th[8] },
                        { w: 60, label: th[9] },
                        { w: 80, label: '' },
                      ].map((col, i) => (
                        <th key={i} style={{
                          width: col.w, padding: '8px 5px',
                          textAlign: col.left ? 'left' : 'center',
                          paddingLeft: col.left ? 8 : 5,
                          fontSize: 9, fontWeight: 700, color: '#e2e8f0',
                          letterSpacing: '0.04em',
                          borderRight: i < 10 ? '1px solid rgba(255,255,255,0.08)' : 'none',
                          whiteSpace: 'nowrap',
                        }}>{col.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((it, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={tdStyle()}><ItemInput value={it.category} onChange={v => upItem(idx,'category',v)} center /></td>
                        <td style={tdStyle('left')}><ItemInput value={it.title} onChange={v => upItem(idx,'title',v)} placeholder={lang==='ko'?'품목명':'Item description'} /></td>
                        <td style={tdStyle()}><ItemInput value={it.spec} onChange={v => upItem(idx,'spec',v)} center placeholder="-" /></td>
                        <td style={tdStyle()}>
                          <ItemInput type="number" value={it.qty} onChange={v => upItem(idx,'qty',v)} center
                            readOnly={!!it.costing}
                            title={it.costing ? '원가계산의 세트 구성수량과 연동됩니다' : undefined} />
                        </td>
                        <td style={tdStyle()}><ItemInput value={it.unit} onChange={v => upItem(idx,'unit',v)} center placeholder={lang==='ko'?'개':'ea'} /></td>
                        <td style={tdStyle()}>
                          <ItemInput type="number" value={it.qty2} onChange={v => upItem(idx,'qty2',v)} center placeholder="-" />
                        </td>
                        <td style={tdStyle()}><ItemInput value={it.unit2} onChange={v => upItem(idx,'unit2',v)} center placeholder="-" /></td>
                        <td style={tdStyle('right')}>
                          <ItemInput type="number" value={it.unitPrice} onChange={v => upItem(idx,'unitPrice',v)} right
                            readOnly={!!it.costing}
                            title={it.costing ? '원가계산 총합 ÷ 세트 구성수량으로 자동 계산됩니다' : undefined} />
                        </td>
                        <td style={{ ...tdStyle('right'), fontWeight: 600, color: '#0f172a', paddingRight: 8 }}>
                          {l.curr(it.amount)}
                        </td>
                        <td style={tdStyle('left')}><ItemInput value={it.note} onChange={v => upItem(idx,'note',v)} placeholder="-" /></td>
                        <td style={{ padding: '4px 3px', textAlign: 'center', verticalAlign: 'middle' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                            <button
                              onClick={() => setCostingIdx(idx)}
                              title={it.costing ? '원가계산 (작성됨)' : '원가계산'}
                              style={{
                                color: it.costing ? '#00B2FF' : '#cbd5e1', padding: 1,
                                background: 'none', border: 'none', display: 'flex', cursor: 'pointer',
                              }}
                              onMouseEnter={e => { e.currentTarget.style.color = '#00B2FF'; }}
                              onMouseLeave={e => { e.currentTarget.style.color = it.costing ? '#00B2FF' : '#cbd5e1'; }}
                            >
                              <Calculator size={11} />
                            </button>
                            <button
                              onClick={() => moveItem(idx, -1)}
                              disabled={idx === 0}
                              title="위로"
                              style={{
                                color: idx === 0 ? '#f1f5f9' : '#cbd5e1', padding: 1,
                                background: 'none', border: 'none', display: 'flex',
                                cursor: idx === 0 ? 'default' : 'pointer',
                              }}
                              onMouseEnter={e => { if (idx !== 0) e.currentTarget.style.color = '#00B2FF'; }}
                              onMouseLeave={e => { e.currentTarget.style.color = idx === 0 ? '#f1f5f9' : '#cbd5e1'; }}
                            >
                              <ChevronUp size={11} />
                            </button>
                            <button
                              onClick={() => moveItem(idx, 1)}
                              disabled={idx === form.items.length - 1}
                              title="아래로"
                              style={{
                                color: idx === form.items.length - 1 ? '#f1f5f9' : '#cbd5e1', padding: 1,
                                background: 'none', border: 'none', display: 'flex',
                                cursor: idx === form.items.length - 1 ? 'default' : 'pointer',
                              }}
                              onMouseEnter={e => { if (idx !== form.items.length - 1) e.currentTarget.style.color = '#00B2FF'; }}
                              onMouseLeave={e => { e.currentTarget.style.color = idx === form.items.length - 1 ? '#f1f5f9' : '#cbd5e1'; }}
                            >
                              <ChevronDown size={11} />
                            </button>
                            <button onClick={() => removeItem(idx)} style={{ color: '#e2e8f0', padding: 2, background: 'none', border: 'none', cursor: 'pointer', display: 'flex' }}
                              onMouseEnter={e => e.currentTarget.style.color='#f87171'}
                              onMouseLeave={e => e.currentTarget.style.color='#e2e8f0'}>
                              <Trash2 size={11} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ padding: '7px 12px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 16 }}>
                <button onClick={addItem}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#00B2FF', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>
                  <Plus size={12} /> {l.addItem}
                </button>
              </div>
            </div>

            {/* 합계 + 메모 */}
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 18 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 9, color: '#94a3b8', marginBottom: 4, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600 }}>{l.memo}</div>
                <textarea value={form.memo} onChange={e => up('memo', e.target.value)}
                  rows={6}
                  placeholder={lang === 'ko' ? '유효기간, 납기일, 특이사항...' : 'Validity, delivery date, notes...'}
                  style={{
                    width: '100%', border: '1px solid #e2e8f0', borderRadius: 8,
                    padding: '8px 10px', fontSize: 11, resize: 'none',
                    outline: 'none', fontFamily: 'inherit', color: '#475569',
                    transition: 'border-color 0.15s',
                  }}
                  onFocus={e => e.target.style.borderColor = '#00B2FF'}
                  onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>
              {/* 합계 박스 */}
              <div style={{ border: '1px solid #e2e8f0', borderLeft: '3px solid #00B2FF', borderRadius: 8, overflow: 'hidden', minWidth: 240 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 14px', fontSize: 11, borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ color: '#64748b' }}>{l.subtotal}</span>
                  <span style={{ fontWeight: 600 }}>{l.curr(form.subtotal)}</span>
                </div>
                {/* 절사 */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 14px', fontSize: 11, borderBottom: '1px solid #f1f5f9', gap: 8 }}>
                  <span style={{ color: '#ef4444', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {lang === 'ko' ? '− 절사' : '− Rounding'}
                  </span>
                  <input
                    type="number"
                    value={form.nego || ''}
                    onChange={e => up('nego', Number(e.target.value) || 0)}
                    placeholder="0"
                    min="0"
                    style={{
                      border: '1px solid #e2e8f0', borderRadius: 5, padding: '2px 6px',
                      fontSize: 11, textAlign: 'right', width: 110, outline: 'none',
                      color: '#ef4444', fontWeight: 600, fontFamily: 'inherit',
                      MozAppearance: 'textfield',
                    }}
                    onFocus={e => e.target.style.borderColor = '#00B2FF'}
                    onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>
                {/* 합계 (공급가액 − 절사) — 절사가 없으면 공급가액과 같으므로 생략 */}
                {form.nego > 0 && form.vatIncluded && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 14px', fontSize: 11, borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                    <span style={{ color: '#475569', fontWeight: 700 }}>{l.sum}</span>
                    <span style={{ fontWeight: 700 }}>{l.curr(form.subtotal - form.nego)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', fontSize: 11, borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: '#64748b' }}>{l.vat}</span>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 9, color: '#94a3b8', cursor: 'pointer' }}>
                      <input type="checkbox" checked={form.vatIncluded} onChange={e => up('vatIncluded', e.target.checked)} style={{ width: 11, height: 11 }} />
                      {l.vatLabel}
                    </label>
                  </div>
                  <span style={{ fontWeight: 600 }}>{l.curr(form.vat)}</span>
                </div>
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '11px 14px',
                  background: 'linear-gradient(135deg, #0f172a 0%, #1e3355 100%)',
                }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: '#fff', letterSpacing: '0.05em' }}>{l.total}</span>
                  <span style={{ fontSize: 13, fontWeight: 800, color: '#00B2FF' }}>{l.curr(form.total)}</span>
                </div>
              </div>
            </div>

            {/* 입금계좌 */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 12,
              background: '#f8fafc', border: '1px solid #e2e8f0',
              borderLeft: '3px solid #00B2FF', borderRadius: 8, padding: '10px 18px',
            }}>
              <span style={{ fontSize: 10, fontWeight: 800, color: '#475569', letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{l.bank}</span>
              <span style={{ fontSize: 11, fontFamily: 'monospace', fontWeight: 700, color: '#0f172a' }}>{s.bank}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap' }}>{s.name}</span>
            </div>

          </div>{/* end doc-content */}
        </div>{/* end estimate-doc */}
      </div>

      {costingIdx !== null && form.items[costingIdx] && (
        <EstimateCostingModal
          item={form.items[costingIdx]}
          onClose={() => setCostingIdx(null)}
          onApply={(unitPrice, setQty, costing) => {
            const idx = costingIdx;
            setForm(f => ({
              ...f,
              items: f.items.map((it, i) => {
                if (i !== idx) return it;
                // 단가 = 원가총합 ÷ 세트구성수량, 수량1 = 세트구성수량.
                // 수량2는 출력할 세트 수 → 기본 1판 (이후 직접 수정 가능)
                const updated = {
                  ...it,
                  unitPrice,
                  qty: String(setQty),
                  qty2: '1',
                  unit2: '판',
                  costing,
                };
                updated.amount = calcAmount(updated);
                return updated;
              }),
            }));
            setCostingIdx(null);
          }}
        />
      )}

      {pickerOpen && (
        <EstimatePicker
          excludeId={savedId || id}
          onClose={() => setPickerOpen(false)}
          onPick={async (srcId) => {
            try {
              const res = await api.get(`/estimates/${srcId}`);
              fillForm(res.data, 'content');
              setPickerOpen(false);
            } catch {
              alert('불러오기에 실패했습니다.');
            }
          }}
        />
      )}
    </div>
  );

  /** 품목 행을 위(-1) 또는 아래(+1)로 한 칸 이동 */
  function moveItem(idx, dir) {
    const to = idx + dir;
    setForm(f => {
      if (to < 0 || to >= f.items.length) return f;
      const items = [...f.items];
      [items[idx], items[to]] = [items[to], items[idx]];
      return { ...f, items };
    });
  }

  function addItem() { setForm(f => ({ ...f, items: [...f.items, emptyItem()] })); }
  function removeItem(idx) { setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) })); }
}

/* ─── 보조 컴포넌트 ─── */
const labelStyle = { fontSize: 9, color: '#94a3b8', width: 66, flexShrink: 0, fontWeight: 600, letterSpacing: '0.03em' };
const cellBase = { display: 'flex', alignItems: 'center', padding: '4px 10px', minHeight: 30 };
const divider = { borderRight: '1px solid #e2e8f0' };

function DualHead({ left, right }) {
  return (
    <>
      <div style={{ background: '#f8fafc', padding: '7px 12px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 6, ...divider }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#00B2FF', flexShrink: 0 }} />
        <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', color: '#334155' }}>{left}</span>
      </div>
      <div style={{ background: '#f8fafc', padding: '7px 12px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#00B2FF', flexShrink: 0 }} />
        <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', color: '#334155' }}>{right}</span>
      </div>
    </>
  );
}

function DualRow({ label, left, right, last }) {
  const border = last ? {} : { borderBottom: '1px solid #f1f5f9' };
  return (
    <>
      <div style={{ ...cellBase, ...border, ...divider }}>
        <span style={labelStyle}>{label}</span>
        <div style={{ flex: 1, minWidth: 0 }}>{left}</div>
      </div>
      <div style={{ ...cellBase, ...border }}>
        <span style={labelStyle}>{label}</span>
        <div style={{ flex: 1, minWidth: 0 }}>{right}</div>
      </div>
    </>
  );
}

function StaticVal({ value, bold, small }) {
  return (
    <span style={{ fontSize: small ? 10 : 11, fontWeight: bold ? 700 : 500, color: '#0f172a' }}>{value}</span>
  );
}

function EditInput({ value, onChange, placeholder }) {
  return (
    <input
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ ...inpStyle }}
      onFocus={e => { e.target.style.borderColor = '#00B2FF'; e.target.style.background = '#f0f9ff'; }}
      onBlur={e => { e.target.style.borderColor = 'transparent'; e.target.style.background = 'transparent'; }}
    />
  );
}

function ItemInput({ value, onChange, placeholder, center, right, type = 'text', readOnly = false, title }) {
  return (
    <input
      type={type}
      value={value ?? ''}
      onChange={e => !readOnly && onChange(e.target.value)}
      readOnly={readOnly}
      title={title}
      placeholder={placeholder}
      style={{
        border: 'none', borderRadius: 3, padding: '2px 4px', fontSize: 11,
        outline: 'none', background: 'transparent', fontFamily: 'inherit', color: '#1e293b',
        width: '100%', textAlign: center ? 'center' : right ? 'right' : 'left',
        MozAppearance: 'textfield',
        color: readOnly ? '#64748b' : '#1e293b',
        cursor: readOnly ? 'not-allowed' : 'auto',
      }}
      onFocus={e => { if (!readOnly) e.target.style.background = '#eff6ff'; }}
      onBlur={e => e.target.style.background = 'transparent'}
    />
  );
}
